
// PlanVsActualManStepper.jsx - Complete Implementation for Man-Hour Workstations
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CircularProgress,
  Checkbox,
  Button,
  Grid,
  Grow,
  Chip,
  Alert,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import axios from "axios";
import PropTypes from "prop-types";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function PlanVsActualManStepper({ selectedDate, machineCode }) {
  const steps = ["Step 1: Shift-log Filtering", "Step 2: Operation-log Filtering", "Step 3: Review & Submit"];
  const [activeStep, setActiveStep] = useState(0);
  const [shiftLogs, setShiftLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedShiftRows, setSelectedShiftRows] = useState([]);
  const [shiftStartDatetime, setShiftStartDatetime] = useState(null);
  const [shiftOutDatetime, setShiftOutDatetime] = useState(null);
  const [selectedOperationRows, setSelectedOperationRows] = useState([]);
  const [checked, setChecked] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Time range for man-hour: 08:00 to next day 08:00
  const startTime = dayjs(selectedDate).hour(7).minute(45).second(0).format("YYYY-MM-DD HH:mm:ss");
  const endTime = dayjs(selectedDate).add(1, "day").hour(7).minute(45).second(0).format("YYYY-MM-DD HH:mm:ss");

  useEffect(() => {
    setChecked(true);
  }, []);

  // ========================================
  // Step 1: Fetch shift logs using MAN-HOUR endpoint
  // ========================================
  useEffect(() => {
    if (activeStep === 0) {
      setLoading(true);
      setError("");
      
      console.log("Fetching Man-Hour Shift Logs:", {
        startTime,
        endTime,
        machineCode,
      });

      axios
        .post(`${API_BASE_URL}/shiftlogForPlanVsActual_ManHour`, { 
          startTime, 
          endTime, 
          machineCode 
        })
        .then((response) => {
          console.log("Man-Hour Shift Logs Response:", response.data);
          setShiftLogs(response.data);
          // Auto-select all shifts initially
          setSelectedShiftRows(response.data.map((_, index) => index + 1));
        })
        .catch((err) => {
          console.error("Error fetching man-hour shift logs:", err);
          setError("Failed to fetch shift logs. Please try again.");
        })
        .finally(() => setLoading(false));
    }
  }, [activeStep, startTime, endTime, machineCode]);

  // Calculate actual time range based on selected shifts
  useEffect(() => {
    if (selectedShiftRows.length > 0 && shiftLogs.length > 0) {
      const selectedData = shiftLogs.filter((_, index) => selectedShiftRows.includes(index + 1));
      
      // Find earliest shift_in_datetime
      const minLoggedIn = selectedData
        .map((row) => dayjs(row.shift_in_datetime))
        .reduce((min, curr) => (curr.isBefore(min) ? curr : min), dayjs(selectedDate).add(1, "day"));
      
      // Find latest shift_out_datetime
      const maxLoggedOut = selectedData
        .map((row) =>
          row.shift_out_datetime
            ? dayjs(row.shift_out_datetime)
            : dayjs(selectedDate).add(1, "day").hour(8).minute(0).second(0)
        )
        .reduce((max, curr) => (curr.isAfter(max) ? curr : max), dayjs(selectedDate).hour(8).minute(0).second(0));
      
      setShiftStartDatetime(minLoggedIn.format("YYYY-MM-DD HH:mm:ss"));
      setShiftOutDatetime(maxLoggedOut.format("YYYY-MM-DD HH:mm:ss"));
    } else {
      setShiftStartDatetime(dayjs(selectedDate).hour(8).minute(0).second(0).format("YYYY-MM-DD HH:mm:ss"));
      setShiftOutDatetime(dayjs(selectedDate).add(1, "day").hour(8).minute(0).second(0).format("YYYY-MM-DD HH:mm:ss"));
    }
  }, [selectedShiftRows, shiftLogs, selectedDate]);

  // ========================================
  // Shift columns for DataGrid (MAN-HOUR specific)
  // ========================================
  const shiftColumns = [
    {
      field: "select",
      headerName: "Select",
      width: 100,
      renderCell: (params) => (
        <Checkbox
          checked={selectedShiftRows.includes(params.row.id)}
          onChange={(e) => {
            const newSelectedRows = [...selectedShiftRows];
            if (e.target.checked) {
              newSelectedRows.push(params.row.id);
            } else {
              const index = newSelectedRows.indexOf(params.row.id);
              if (index > -1) newSelectedRows.splice(index, 1);
            }
            setSelectedShiftRows(newSelectedRows);
          }}
        />
      ),
    },
    { 
      field: "emp_id", 
      headerName: "Employee ID", 
      width: 120 
    },
    { 
      field: "emp_name", 
      headerName: "Employee Name", 
      width: 200 
    },
    {
      field: "shift_in_datetime",
      headerName: "Shift In",
      width: 180,
      renderCell: (params) => (params.value ? dayjs(params.value).format("YYYY-MM-DD HH:mm") : "N/A"),
    },
    {
      field: "shift_out_datetime",
      headerName: "Shift Out",
      width: 180,
      renderCell: (params) => (params.value ? dayjs(params.value).format("YYYY-MM-DD HH:mm") : "N/A"),
    },
    { 
      field: "supervisor_name", 
      headerName: "Supervisor", 
      width: 150 
    },
    { 
      field: "station_code", 
      headerName: "Station", 
      width: 100 
    },
    { 
      field: "remarks", 
      headerName: "Remarks", 
      width: 250 
    },
  ];

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  // Render Step 1 content
  let stepOneContent;
  if (loading) {
    stepOneContent = (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (error) {
    stepOneContent = (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  } else {
    stepOneContent = (
      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={shiftLogs.map((log, index) => ({ id: index + 1, ...log }))}
          columns={shiftColumns}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
          checkboxSelection={false}
          disableSelectionOnClick
        />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Summary Cards */}
        <Box sx={{ padding: "0px 0px 10px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in={checked} timeout={1000}>
                <Card sx={{ backgroundColor: "#e3f2fd", borderRadius: "8px", boxShadow: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PeopleIcon sx={{ color: "#0d47a1" }} />
                      <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
                        Man-Hour Station
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: "#0d47a1", fontSize: "0.9rem" }}>
                      Multiple employees work simultaneously
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Grow in={checked} timeout={1500}>
                <Card sx={{ backgroundColor: "#e3f2fd", borderRadius: "8px", boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
                      Summary Date
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#0d47a1" }}>
                      {dayjs(selectedDate).format("DD MMMM YYYY")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Grow in={checked} timeout={2000}>
                <Card sx={{ backgroundColor: "#e3f2fd", borderRadius: "8px", boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
                      Work Station
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#0d47a1" }}>
                      {machineCode}
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Grow in={checked} timeout={2500}>
                <Card sx={{ backgroundColor: "#e3f2fd", borderRadius: "8px", boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
                      Time Range
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#0d47a1", fontSize: "0.85rem" }}>
                      From: {shiftStartDatetime ? dayjs(shiftStartDatetime).format("HH:mm") : "08:00"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#0d47a1", fontSize: "0.85rem" }}>
                      To: {shiftOutDatetime ? dayjs(shiftOutDatetime).format("HH:mm") : "08:00"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          </Grid>
        </Box>

        {/* Stepper */}
        <Box sx={{ width: "100%", mb: 2 }}>
          <Grow in={checked} timeout={1500}>
            <Stepper activeStep={activeStep}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grow>
        </Box>

        {/* Step Content */}
        {activeStep === 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                icon={<PeopleIcon />}
                label={`${shiftLogs.length} Employee Shift${shiftLogs.length !== 1 ? 's' : ''} Found`}
                color="primary"
                variant="outlined"
              />
              <Chip 
                label={`${selectedShiftRows.length} Selected`}
                color="success"
              />
            </Box>
            {stepOneContent}
          </Box>
        )}

        {activeStep === 1 && (
          <PlanVsActualManOpeLog
            shiftStartDatetime={shiftStartDatetime}
            shiftOutDatetime={shiftOutDatetime}
            machineCode={machineCode}
            setSelectedOperationLogs={setSelectedOperationRows}
          />
        )}

        {activeStep === 2 && (
          <ManHourTableGenerate
            selectedShiftRows={shiftLogs.filter((_, index) => selectedShiftRows.includes(index + 1))}
            selectedOperationRows={selectedOperationRows}
            shiftOutDatetime={shiftOutDatetime}
            machineCode={machineCode}
            shiftStartDatetime={shiftStartDatetime}
            selectedDate={selectedDate}
            setSnackbar={setSnackbar}
          />
        )}

        {/* Navigation Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <Button onClick={handleBack} disabled={activeStep === 0}>
            Back
          </Button>
          <Button 
            onClick={handleNext} 
            disabled={activeStep === steps.length - 1}
            variant="contained"
          >
            Next
          </Button>
        </Box>
      </CardContent>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
}

PlanVsActualManStepper.propTypes = {
  selectedDate: PropTypes.object.isRequired,
  machineCode: PropTypes.string.isRequired,
};


// ============================================
// Step 2 Component: Operation Log Filtering
// ============================================
function PlanVsActualManOpeLog({ shiftStartDatetime, shiftOutDatetime, machineCode, setSelectedOperationLogs }) {
  const [operationLogs, setOperationLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedOperationRows, setSelectedOperationRows] = useState([]);

  // ========================================
  // Fetch operation logs using MAN-HOUR endpoint
  // (Only JOB_STARTED rows)
  // ========================================
  useEffect(() => {
    if (shiftStartDatetime && shiftOutDatetime && machineCode) {
      console.log("Fetching Man-Hour Operation Logs:", {
        shiftStartDatetime,
        shiftOutDatetime,
        machineCode,
      });

      setLoading(true);
      setError("");

      axios
        .post(`${API_BASE_URL}/operationLogForPlanVsActual_ManHour`, {
          shiftStartDatetime: dayjs(shiftStartDatetime).format("YYYY-MM-DD HH:mm:ss"),
          shiftOutDatetime: dayjs(shiftOutDatetime).format("YYYY-MM-DD HH:mm:ss"),
          machineCode,
        })
        .then((response) => {
          console.log("Man-Hour Operation Logs Response:", response.data);

          // Add IDs and auto-select all rows (all are JOB_STARTED for man-hour)
          const logsWithIds = response.data.map((row, index) => ({
            ...row,
            id: index + 1,
          }));
          setOperationLogs(logsWithIds);

          // Auto-select all rows
          setSelectedOperationRows(logsWithIds);
          setSelectedOperationLogs(logsWithIds);
        })
        .catch((err) => {
          console.error("Error fetching man-hour operation logs:", err);
          setError("Failed to fetch operation logs. Please try again.");
        })
        .finally(() => setLoading(false));
    }
  }, [shiftStartDatetime, shiftOutDatetime, machineCode, setSelectedOperationLogs]);

  // Handle checkbox selection changes
  const handleCheckboxChange = (rowId, isChecked) => {
    const updatedRows = isChecked
      ? [...selectedOperationRows, operationLogs.find((row) => row.id === rowId)]
      : selectedOperationRows.filter((row) => row.id !== rowId);

    setSelectedOperationRows(updatedRows);
    setSelectedOperationLogs(updatedRows);
  };

  // ========================================
  // Define columns for the DataGrid (MAN-HOUR specific)
  // ========================================
  const columns = [
    {
      field: "select",
      headerName: "Select",
      width: 80,
      renderCell: (params) => (
        <Checkbox
          checked={selectedOperationRows.some((row) => row.id === params.row.id)}
          onChange={(e) => handleCheckboxChange(params.row.id, e.target.checked)}
        />
      ),
    },
    { field: "emp_id", headerName: "Employee ID", width: 120 },
    { field: "emp_name", headerName: "Name", width: 150 },
    { field: "job_number", headerName: "Job Number", width: 150 },
    { field: "customer", headerName: "Customer", width: 200 },
    { field: "product", headerName: "Product", width: 200 },
    { field: "main_operation", headerName: "Operation", width: 200 },
    { field: "prod_qty", headerName: "Production", width: 120 },
    { field: "waste_qty", headerName: "Waste", width: 100 },
    {
      field: "start_datetime",
      headerName: "Start",
      width: 180,
      renderCell: (params) => (params.value ? dayjs(params.value).format("YYYY-MM-DD HH:mm") : "N/A"),
    },
    {
      field: "end_datetime",
      headerName: "End",
      width: 180,
      renderCell: (params) => (params.value ? dayjs(params.value).format("YYYY-MM-DD HH:mm") : "N/A"),
    },
    { field: "duration", headerName: "Duration", width: 120 },
    { field: "remarks", headerName: "Remarks", width: 200 },
  ];

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1" }}>
          Step 2: Operation-log Filtering (JOB_STARTED Rows Only)
        </Typography>
        <Chip 
          icon={<WorkIcon />}
          label={`${operationLogs.length} Job${operationLogs.length !== 1 ? 's' : ''} Found`}
          color="primary"
          variant="outlined"
        />
        <Chip 
          label={`${selectedOperationRows.length} Selected`}
          color="success"
        />
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <DataGrid
          rows={operationLogs}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
          checkboxSelection={false}
          disableSelectionOnClick
          sx={{
            height: 400,
            "& .MuiDataGrid-row": {
              backgroundColor: "#e8f4f8",
            },
          }}
        />
      )}
    </Box>
  );
}

// ============================================
// Step 3 Component: Review & Submit
// ============================================
function ManHourTableGenerate({
  selectedShiftRows,
  selectedOperationRows,
  shiftOutDatetime,
  machineCode,
  shiftStartDatetime,
  selectedDate,
  setSnackbar,
}) {
  const [submitting, setSubmitting] = useState(false);

  // ========================================
  // Handle submission to MAN-HOUR endpoint
  // ========================================
  const handleSubmit = async () => {
    setSubmitting(true);

    // Prepare data for submission
    const payload = {
      machineCode,
      shiftStartDatetime,
      shiftOutDatetime,
       selectedDate: dayjs(selectedDate).format("YYYY-MM-DD"),  // ← add this
      editableShiftRows: selectedShiftRows.map(row => ({
        emp_id: row.emp_id,
        emp_name: row.emp_name,
        shift_in_datetime: row.shift_in_datetime,
        shift_out_datetime: row.shift_out_datetime,
        supervisor_id: row.supervisor_id || null,
        supervisor_name: row.supervisor_name || '',
        remarks: row.remarks || '',
      })),
      editableOperationRows: selectedOperationRows.map(row => ({
        emp_id: row.emp_id,
        emp_name: row.emp_name,
        job_number: row.job_number,
        customer: row.customer,
        product: row.product,
        main_operation: row.main_operation,
        operation_code: row.operation_code,
        operation_description: row.operation_description,
        operation_type: row.operation_type,
        start_datetime: row.start_datetime,
        end_datetime: row.end_datetime,
        duration_hours: row.duration_hours,
        prod_qty: row.prod_qty || 0,
        waste_qty: row.waste_qty || 0,
        remarks: row.remarks || '',
      })),
    };

    console.log("Submitting Man-Hour Plan vs Actual:", payload);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/generateplanvsactual_ManHour`, 
        payload
      );

      console.log("Success:", response.data);
      
      setSnackbar({
        open: true,
        message: "Man-Hour Plan vs Actual generated successfully!",
        severity: "success",
      });

      // Optional: Redirect after success
      // setTimeout(() => {
      //   window.location.href = "/planvsactual/view"; // Adjust to your route
      // }, 2000);

    } catch (error) {
      console.error("Error:", error);
      
      setSnackbar({
        open: true,
        message: error.response?.data?.error || "Failed to generate report. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: "bold", color: "#0d47a1", mb: 2 }}>
        Step 3: Review & Submit
      </Typography>
      
      <Card sx={{ mb: 2, backgroundColor: "#f5f5f5" }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
            Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">Workstation:</Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>{machineCode}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">Date:</Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {dayjs(selectedDate).format("YYYY-MM-DD")}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">Employee Shifts:</Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {selectedShiftRows.length}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="textSecondary">Jobs:</Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {selectedOperationRows.length}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          ℹ️ Man-hour data will be grouped by job, showing all employees who worked on each job with their combined quantities.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Employee list format: "1289-Punsisi, 2029-Nirosha, 7788-Gamini"
        </Typography>
      </Alert>

      <Button
        variant="contained"
        color="primary"
        size="large"
        fullWidth
        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
        disabled={submitting || selectedShiftRows.length === 0}
        sx={{ mt: 2 }}
        onClick={handleSubmit}
      >
        {submitting ? "Generating..." : "Generate Man-Hour Plan vs Actual"}
      </Button>
    </Box>
  );
}